export interface TouchpointTemplateValues {
  AlarmType: { [key: number]: string };
  EwsSyncStatus?: { [key: number]: string };
  TouchpointType: { [key: number]: string };
  RecurrencePattern?: { [key: number]: string };
}

export const ACCOUNT_DASHBOARD_ROUTES = { customer: '/accounts', lead: '/accounts-leads' } as const;

export const ACCOUNT_DASHBOARD_SECTIONS = { notes: 'notes' } as const;

export type AccountDashboardSection = typeof ACCOUNT_DASHBOARD_SECTIONS[keyof typeof ACCOUNT_DASHBOARD_SECTIONS];

export const TOUCHPOINT_NAVIGATION_PARAMS = {
  section: 'section',
  openTouchpointId: 'openTouchpointId',
  calendarDate: 'calendarDate',
  employeeId: 'employeeId'
} as const;

export const TOUCHPOINT_DETAIL_ACTIONS = {
  lastVisited: 'lastVisited',
  accountDashboard: 'accountDashboard'
} as const;

export type TouchpointDetailAction = typeof TOUCHPOINT_DETAIL_ACTIONS[keyof typeof TOUCHPOINT_DETAIL_ACTIONS];

export interface TouchpointDetailResult {
  deleted?: boolean;
  edited?: boolean;
  action?: TouchpointDetailAction;
}

export const recordTypeIcons = {
  1: '../../../../../assets/images/phone.svg',
  2: '../../../../../assets/images/location.svg',
  3: '../../../../../assets/images/list.svg'
};

export const touchpointTypeIcons = {
  1: 'meeting',
  2: 'call',
  3: 'appointment',
  4: 'task'
};

export const touchpointTypeMaterialIcons = {
  1: 'groups',
  2: 'call',
  3: 'date_range',
  4: 'list_alt'
};

export const touchpointTypeColors = {
  1: 'blue',
  2: 'green',
  3: 'pink',
  4: 'orange'
};

export enum TouchpointType {
  Meeting = '1',
  Call = '2',
  Appointment = '3',
  Task = '4'
}

export interface TouchpointContact {
  Name: string;
  Phone: string;
  Email: string;
}

export interface TouchpointResponse {
  count: number;
  total: number;
  touchpoints: Touchpoint[];
}

export class Touchpoint {
  id?: string;
  subject: string;
  touchpointType: number;
  firstContactName?: string;
  customerName?: string;
  companyName?: string;
  isOutlookOnly?: boolean;
  isImpactGeneratedTouchpoint?: boolean;
  isArchived?: boolean;
  tollNumber?: string;
  dialInUrl?: string;
  isRecurring?: boolean;
  seriesMasterId?: string;
  joinUrl?: string;
  touchpointStartDateLabel?: string | Date;
  touchpointEndDateLabel?: string | Date;
  touchpointTypeLabel?: string;
  touchpointTypeIcon?: string;
  touchpointTypeMaterialIcon?: string;
  touchpointTypeColor?: string;
  companyRedirect?: string | null;
  customerId?: string;
  leadId?: string;
  contacts: Contact[];
  startDate: string;
  endDate: string;
  alarmType: number;
  notes: string;
  location: string;
  calendarId?: string;
  firstContactDisplay?: string;
  moreContactsLength?: number;
  accountManagerId?: string;
  employeeId?: string;
  updateRecurrence?: boolean;
  recurrencePattern?: string;
  recurrenceInterval?: number;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  dayOfMonth?: number;
  recurrenceStartDate?: string;
  recurrenceEndDate?: string | null;
  recurrenceStart?: string;
  recurrenceEnd?: string | null;
  numberOfOccurrences?: number;
  lastTouchpoint?: string;
  hasAttachments?: boolean;
  attachments?: TouchpointAttachment[];
}

export interface TouchpointComment {
  id: string;
  touchpointId: string;
  name: string;
  date: string;
  detail: string;
}

export interface TouchpointAttachment {
  id: string | null;
  name: string;
  contentType?: string;
  size?: number;
  file?: File;
}

export interface TouchpointSaveResult {
  touchpoint: Touchpoint;
  warningMessage: string;
}

export interface TouchpointDelete {
  touchpointId: string;
  warningMessage: string;
}

export interface Customer {
  id: string;
  bpNumber?: string;
  name?: string;
  displayAddress?: string;
  currency?: string;
  isLead?: boolean;
}

export interface Contact {
  id: string;
  name?: string;
  phoneNumber?: string;
  isDeleted?: boolean;
}

export interface TouchpointListPayload {
  touchpointTypes: number[];
  resultLimit?: number;
  customerId?: string;
  date: {
    startDate: string;
    endDate: string;
  };
  leadId?: string;
  employeeId?: string;
}

export const RECURRENCE_DAYS = [
  { value: 'Mon', fieldName: 'monday' },
  { value: 'Tue', fieldName: 'tuesday' },
  { value: 'Wed', fieldName: 'wednesday' },
  { value: 'Thu', fieldName: 'thursday' },
  { value: 'Fri', fieldName: 'friday' },
  { value: 'Sat', fieldName: 'saturday' },
  { value: 'Sun', fieldName: 'sunday' },
] as const satisfies readonly { value: string, fieldName: keyof Touchpoint }[];

export type RecurrenceDayField = (typeof RECURRENCE_DAYS)[number]['fieldName'];

export interface CalendarEvent {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  category: string;
  hasAlert: boolean;
  hasRecurrence: boolean;
  hasAttachment: boolean;
}

export interface CalendarEventFilterOption {
  label: string;
  type: string;
  checked: boolean;
  color: string;
  backgroundColor: string;
}

export const CALENDAR_VIEWS = {
  day: 'day',
  week: 'week',
  month: 'month'
} as const;

export type CalendarView = typeof CALENDAR_VIEWS[keyof typeof CALENDAR_VIEWS];

export const TOUCHPOINTS_TABS = {
  calendar: 0,
  manage: 1
} as const;

export type TouchpointsTab = typeof TOUCHPOINTS_TABS[keyof typeof TOUCHPOINTS_TABS];

export interface CalendarDateRange {
  StartDate: Date;
  EndDate: Date;
}

export interface TimeRangeSelectedEvent {
  date: string;
  startTime: string;
  endTime: string;
}
